import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Card,
  CardMedia,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import { images } from '../assets/images';

// Blog post data
const blogPostsData = {
  'innovation-in-farming': {
    id: 'innovation-in-farming',
    title: 'Innovation in Farming: Transforming Agriculture Through Technology',
    image: images.analytics || '',
    author: 'Dr. John Mwangi',
    date: 'March 15, 2023',
    category: 'Technology',
    tags: ['Innovation', 'Technology', 'Smart Farming', 'Digital Agriculture'],
    summary: 'Discover the latest agricultural technologies transforming farming in Kenya and beyond.',
    content: `
    # Innovation in Farming: Transforming Agriculture Through Technology

    The agricultural sector is experiencing a technological revolution that is changing how farmers work, plan, and succeed. These innovations are particularly important for countries like Kenya, where agriculture remains a significant part of the economy and employs a large portion of the population.

    ## Smart Farming Technologies

    Smart farming, also known as precision agriculture, uses IoT sensors, drones, GPS mapping, and data analytics to monitor crops, optimize resource use, and increase yields. For Kenyan farmers, these technologies can help address challenges like water scarcity, climate change, and limited access to information.

    IoT sensors placed throughout fields can monitor soil moisture, temperature, and nutrient levels in real-time. This data helps farmers make informed decisions about irrigation and fertilization, reducing waste and optimizing crop health.

    Drones equipped with multispectral cameras can survey large areas quickly, identifying issues like pest infestations, disease, or irrigation problems before they become serious. This early detection capability is especially valuable for smallholder farmers who might otherwise miss problems until significant damage has occurred.

    ## Mobile Applications for Farmers

    Mobile phone penetration in Kenya is among the highest in Africa, making smartphone apps an accessible technology for many farmers. These applications provide valuable services including:

    - Market price information to help farmers decide when and where to sell their produce
    - Weather forecasts and climate information for better planning
    - Pest and disease identification through photo analysis
    - Access to agricultural extension services and expert advice
    - Financial services including mobile banking and microloans

    ## Blockchain for Agricultural Supply Chains

    Blockchain technology is creating more transparent and efficient agricultural supply chains. By recording transactions on an immutable digital ledger, blockchain can:

    - Track produce from farm to consumer, ensuring food safety
    - Verify organic or fair-trade certifications
    - Eliminate middlemen, allowing farmers to capture more value
    - Facilitate faster payments to farmers
    - Reduce fraud and corruption in agricultural subsidies

    ## AI and Machine Learning

    Artificial intelligence and machine learning are powering the next generation of agricultural tools. These technologies can:

    - Analyze satellite imagery to predict crop yields
    - Identify optimal planting patterns based on soil and climate data
    - Provide personalized recommendations for crop varieties and farming practices
    - Power autonomous farming equipment that can plant, weed, and harvest with minimal human intervention

    ## Challenges and Opportunities

    While these technologies hold tremendous promise, there are challenges to their widespread adoption in Kenya. Issues like infrastructure limitations, digital literacy, and the initial cost of technology can be barriers, particularly for smallholder farmers.

    However, innovative business models like equipment sharing, pay-as-you-go services, and cooperative ownership are helping to overcome these challenges. Government programs and NGO initiatives are also playing a crucial role in making agricultural technology more accessible.

    ## The Future of Farming

    As these technologies become more affordable and accessible, they have the potential to transform Kenyan agriculture into a more productive, sustainable, and profitable sector. Young people who might otherwise abandon farming for urban opportunities are finding that technology makes agriculture more appealing and less labor-intensive.

    The future of farming in Kenya will likely be a blend of traditional knowledge and cutting-edge technology, creating resilient food systems that can feed a growing population while adapting to climate change and other challenges.

    By embracing these innovations, Kenyan farmers can lead the way in showing how technology can revolutionize agriculture across Africa and beyond.
    `,
    relatedPosts: ['data-analytics', 'sustainable-practices']
  },
  'data-analytics': {
    id: 'data-analytics',
    title: 'Data Analytics: Using Information to Improve Farm Yields',
    image: images.innovation || '',
    author: 'Jane Kamau',
    date: 'April 2, 2023',
    category: 'Data Science',
    tags: ['Data Analytics', 'Farm Management', 'Yields', 'Decision Making'],
    summary: 'How data analytics is revolutionizing farm management and improving yields across Kenya.',
    content: `
    # Data Analytics: Using Information to Improve Farm Yields

    In the age of information, data has become one of the most valuable resources for farmers. Agricultural data analytics involves collecting, processing, and analyzing farm data to make better decisions and improve overall productivity. For Kenyan farmers, this approach represents a significant shift from traditional farming methods to evidence-based agriculture.

    ## The Value of Farm Data

    Farm data comes in many forms, including:

    - Soil composition and health metrics
    - Weather patterns and historical climate data
    - Crop performance records
    - Input costs and usage statistics
    - Market prices and trends
    - Livestock health and productivity information

    When properly analyzed, this data provides insights that can dramatically improve farm operations, resource allocation, and profitability.

    ## Data Collection Methods

    Modern farms use various technologies to collect data:

    - Soil sensors that measure moisture, temperature, and nutrient levels
    - Weather stations that track local climate conditions
    - GPS-enabled equipment that maps field activities
    - Yield monitors on harvesters that measure productivity by location
    - Mobile apps for manual data entry and observation recording
    - Satellite and drone imagery showing crop health and development

    ## Turning Data Into Action

    The real value of data comes from analysis and application. Advanced analytics tools can:

    - Identify patterns that may not be obvious to the human eye
    - Predict potential problems before they affect crops
    - Optimize resource use by applying inputs only where and when needed
    - Compare performance across seasons, fields, or crop varieties
    - Calculate the return on investment for different farming practices

    ## Case Study: Precision Agriculture in Kenya's Coffee Farms

    Coffee farmers in central Kenya have begun using data analytics to optimize their production. By analyzing soil data, they can apply fertilizers precisely where needed, reducing costs and environmental impact while improving yields. Weather data helps them time their irrigation and pest control measures more effectively.

    The results have been impressive: farms using these techniques have seen yields increase by 30% while reducing input costs by 20%.

    ## Accessible Data Analytics

    While sophisticated data systems may seem out of reach for many Kenyan farmers, simplified versions are becoming increasingly accessible:

    - Mobile apps that analyze photos of crops to detect diseases
    - SMS services that provide localized weather forecasts and market information
    - Community data sharing through farmer cooperatives
    - Government and NGO programs that provide data collection and analysis services

    ## Challenges in Agricultural Data Analytics

    Several challenges must be addressed for data analytics to reach its full potential in Kenyan agriculture:

    - Data literacy and training for farmers
    - Connectivity and technology infrastructure in rural areas
    - Data ownership, privacy, and security concerns
    - Integration of traditional farming knowledge with data-driven approaches
    - Cost and accessibility of data collection tools

    ## The Future of Farm Data

    As technology becomes more affordable and user-friendly, data analytics will likely become a standard part of farming in Kenya. Initiatives to create shared data platforms, where farmers can contribute and access anonymized data, will accelerate this trend.

    The future may also see the development of Kenya-specific algorithms and analytics tools designed for local crops, conditions, and farming practices. This localization will make data analytics even more valuable for Kenyan farmers.

    By embracing data-driven farming, Kenya has the opportunity to leapfrog traditional agricultural development paths and create a more efficient, productive, and sustainable agricultural sector.
    `,
    relatedPosts: ['innovation-in-farming', 'sustainable-practices']
  },
  'sustainable-practices': {
    id: 'sustainable-practices',
    title: 'Sustainable Practices: Eco-friendly Farming Methods for the Future',
    image: images.sustainable || '',
    author: 'Elizabeth Wanjiru',
    date: 'May 18, 2023',
    category: 'Sustainability',
    tags: ['Sustainable Farming', 'Eco-friendly', 'Conservation', 'Climate Change'],
    summary: 'Exploring eco-friendly farming methods that maintain productivity while protecting the environment.',
    content: `
    # Sustainable Practices: Eco-friendly Farming Methods for the Future

    Sustainable agriculture focuses on meeting today's food needs without compromising the ability of future generations to meet theirs. This approach is particularly important in Kenya, where climate change, soil degradation, and water scarcity threaten agricultural productivity. By adopting sustainable practices, Kenyan farmers can protect their land while maintaining or even improving yields.

    ## Conservation Agriculture

    Conservation agriculture is based on three principles:

    1. **Minimal soil disturbance**: Reducing or eliminating tillage helps maintain soil structure and prevents erosion.
    2. **Permanent soil cover**: Keeping the soil covered with crops or mulch protects it from erosion and water loss.
    3. **Crop rotation and diversification**: Growing different crops in sequence improves soil health and breaks pest cycles.

    Farmers practicing conservation agriculture in Kenya's arid and semi-arid regions have seen improved water retention in their soils, allowing them to grow crops even during drier periods.

    ## Agroforestry

    Agroforestry integrates trees with crop and livestock systems. Benefits include:

    - Improved soil fertility as tree leaves decompose
    - Additional income from tree products like fruits and timber
    - Enhanced biodiversity and ecosystem services
    - Carbon sequestration to mitigate climate change
    - Protection from wind and water erosion

    The Greenbelt Movement in Kenya has promoted agroforestry for decades, with thousands of farmers now incorporating trees into their agricultural systems.

    ## Water Conservation Techniques

    In a country where water scarcity is a growing concern, efficient water use is essential:

    - **Rainwater harvesting**: Collecting rainwater from roofs and other surfaces for later use
    - **Drip irrigation**: Delivering water directly to plant roots, minimizing waste
    - **Zai pits**: Small planting pits that concentrate water and nutrients where plants need them
    - **Terracing**: Creating level platforms on hillsides to reduce runoff and erosion

    These techniques allow farmers to produce crops with less water, making agriculture viable even in drier regions.

    ## Organic Farming

    Organic farming eliminates synthetic fertilizers and pesticides, focusing instead on:

    - Composting and organic matter management
    - Biological pest control methods
    - Cover cropping and green manures
    - Crop rotation for pest and disease management

    Kenya has a growing organic farming sector, with products commanding premium prices in both domestic and export markets.

    ## Integrated Pest Management (IPM)

    IPM combines various techniques to control pests while minimizing environmental impact:

    - Monitoring pest populations to determine when action is needed
    - Using natural predators and biological controls
    - Employing cultural practices that discourage pests
    - Applying pesticides only as a last resort and in targeted ways

    This approach reduces chemical use while effectively protecting crops from damage.

    ## Climate-Smart Agriculture

    Climate-smart agriculture addresses the dual challenges of food security and climate change:

    - Selecting crop varieties that can withstand drought, heat, or floods
    - Diversifying farm systems to reduce risk
    - Improving carbon sequestration in soils and vegetation
    - Adopting renewable energy sources for farm operations

    Kenya's Climate-Smart Agriculture Strategy aims to increase the adoption of these practices nationwide.

    ## Community-Based Approaches

    Sustainable agriculture is often most successful when whole communities adopt it together:

    - Farmer field schools where peers learn from each other
    - Community-managed natural resources like forests and watersheds
    - Cooperative marketing of sustainably produced goods
    - Seed saving and exchange networks to preserve local varieties

    These collaborative approaches build resilience at both farm and landscape levels.

    ## The Economic Case for Sustainability

    Many farmers are concerned that sustainable practices may reduce profits, but evidence suggests otherwise:

    - Reduced input costs for fertilizers and pesticides
    - Premium prices for organically produced goods
    - Improved resilience to weather extremes and market fluctuations
    - Diversified income streams from multiple crops and products
    - Long-term productivity gains from healthier soils

    By thinking beyond short-term yields to long-term farm health, sustainable agriculture often proves more economically viable over time.

    ## Looking Forward

    As Kenya faces increasing environmental challenges, sustainable agriculture will become not just an option but a necessity. By combining traditional knowledge with modern ecological understanding, Kenyan farmers can create productive, resilient agricultural systems that will sustain communities for generations to come.
    `,
    relatedPosts: ['innovation-in-farming', 'data-analytics']
  }
};

const BlogPost: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  
  // Find the blog post data based on the URL parameter
  const post = postId ? blogPostsData[postId as keyof typeof blogPostsData] : null;
  
  // If post not found, show a message
  if (!post) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography variant="h4">Blog post not found</Typography>
        <Button 
          component={Link} 
          to="/" 
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Home
        </Button>
      </Container>
    );
  }
  
  // Convert markdown-style headings to proper Typography components
  const formatContent = (content: string) => {
    const paragraphs = content.split('\n\n').map((paragraph, index) => {
      if (paragraph.trim().startsWith('# ')) {
        return (
          <Typography key={index} variant="h3" gutterBottom sx={{ mt: 4, mb: 2 }}>
            {paragraph.replace('# ', '')}
          </Typography>
        );
      } else if (paragraph.trim().startsWith('## ')) {
        return (
          <Typography key={index} variant="h4" gutterBottom sx={{ mt: 3, mb: 2 }}>
            {paragraph.replace('## ', '')}
          </Typography>
        );
      } else if (paragraph.trim().startsWith('- ')) {
        const items = paragraph.split('\n').map(item => item.replace('- ', ''));
        return (
          <List key={index} sx={{ mb: 2, pl: 2 }}>
            {items.map((item, i) => (
              <ListItem key={i} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        );
      } else {
        return (
          <Typography key={index} variant="body1" paragraph>
            {paragraph}
          </Typography>
        );
      }
    });
    
    return paragraphs;
  };
  
  return (
    <Box sx={{ bgcolor: 'background.default', pt: 4, pb: 8 }}>
      {/* Hero Section with Image */}
      <Box 
        sx={{ 
          height: '40vh', 
          width: '100%', 
          position: 'relative',
          backgroundImage: `url(${post.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mb: 4,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <Container 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography variant="h2" color="white" gutterBottom>
            {post.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
            <PersonIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle1" sx={{ mr: 3 }}>
              {post.author}
            </Typography>
            <CalendarTodayIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle1">
              {post.date}
            </Typography>
          </Box>
        </Container>
      </Box>
      
      <Container>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 4 }}
        >
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            Home
          </Link>
          <Link to="/blog" style={{ textDecoration: 'none', color: 'inherit' }}>
            Blog
          </Link>
          <Typography color="text.primary">{post.title}</Typography>
        </Breadcrumbs>
        
        {/* Main Content Section */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 4, overflow: 'hidden' }}>
              <CardMedia
                component="img"
                height="400"
                image={post.image}
                alt={post.title}
              />
            </Card>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {post.summary}
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 4 }} />
            
            <Box>
              {formatContent(post.content)}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, mb: 2 }}>
              <Button startIcon={<ShareIcon />}>
                Share
              </Button>
              <Button startIcon={<BookmarkIcon />}>
                Save
              </Button>
            </Box>
          </Grid>
          
          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                About the Author
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ width: 64, height: 64, mr: 2 }}>
                  {post.author.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">{post.author}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expert in {post.category}
                  </Typography>
                </Box>
              </Box>
            </Paper>
            
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Categories
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItemButton>
                  <ListItemText primary={post.category} />
                </ListItemButton>
              </List>
            </Paper>
            
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Tags
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {post.tags.map((tag, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      bgcolor: 'action.selected',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {tag}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
            
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Related Posts
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {post.relatedPosts.map((relatedPostId) => {
                const relatedPost = blogPostsData[relatedPostId as keyof typeof blogPostsData];
                return (
                  <Box key={relatedPostId} sx={{ mb: 2 }}>
                    <Link 
                      to={`/blog/${relatedPostId}`} 
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography color="primary" variant="subtitle1">
                        {relatedPost.title}
                      </Typography>
                    </Link>
                    <Typography variant="body2" color="text.secondary">
                      {relatedPost.summary.substring(0, 80)}...
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BlogPost; 